/**
 * Filters component renders referee (and optionally contact) filter selectors with dynamic field
 * inputs based on the chosen filter type. It builds a payload and passes it back via
 * `handleFilterChange` so parent components can aggregate and execute searches.
 *
 * Data for dropdowns is sourced from slice in Redux (tags). Each selectable filter encodes both an id
 * and an input type in its `ObjectId` (e.g. `Address/text-field`,).
 * Based on that type, the appropriate input (text, select) is rendered by `FieldTypeGenerator`.
 *
 * After a user chooses a filter and provides a value, clicking "Add Filter" emits a
 * normalized object with field identifiers and values. "Clear Filters" resets existing detail filters,
 * while "Search" triggers `searchPerson` using the current accumulated filters.
 *
 * @component
 * @param {Object} props
 * @param {Function} props.handleFilterChange Callback invoked with a filter payload when a new filter is added.
 * @param {Array<Object>} props.tableData Current table data; used to optionally reset form state when empty.
 * @param {Function} props.searchPerson Executes the search action using accumulated filters.
 * @param {Array<Object>} props.detailFilter Array of currently applied detailed filters (controls button disabled state).
 * @param {Function} props.clearDetailFilter Clears all currently applied detailed filters.
 * @returns {JSX.Element} Rendered filter UI.
 */

import { Col, Divider, Flex, Row, Space, Spin, Tooltip, Typography } from "antd";
import Button from "@ui/form-controls/button";
import { useEffect, useMemo, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

import Select from "@ui/form-controls/select";
import FieldTypeGenerator from "../shared/field-type-generator";

import { REFEREE_FILTER_OPTIONS, CONTACT_FILTER_OPTIONS } from "./constants";
import { FIELD_TYPES, trueFalseOptions } from "../shared/constants";

import { detailClientSearchClear } from "@redux/actionCreators";

const { Title } = Typography;

const { TEXT_FIELD, SELECT_FIELD } = FIELD_TYPES;

const defaultSelectedField = {
  selectedFieldId: null, // e.g., "ContactType"
  selectedFieldName: null, // e.g., "Contact Type"
  selectedFieldType: null, // e.g., "text", "input-select"
  selectedFieldOptions: [], // e.g., [{ Name: <selectedFieldName>, ObjectId: <selectedFieldId/FieldType> }, ...]
  selectedFieldDropDownValue: null,
  selectedFieldTextValue: null
};

const Filters = ({ handleFilterChange, searchPerson, detailFilter, clearDetailFilter }) => {
  const dispatch = useDispatch();

  const [selectedRefereeFilterField, setSelectedRefereeFilterField] =
    useState(defaultSelectedField);
  const [contactSelectKey, setContactSelectKey] = useState(0);
  const [refereeSelectKey, setRefereeSelectKey] = useState(0);
  const [selectedContactFilterField, setSelectedContactFilterField] =
    useState(defaultSelectedField);

  const candidateModalReducer = useSelector(rdc => rdc?.candidateModalReducer, shallowEqual);
  const candidateReducer = useSelector(state => state.candidateReducer || null, shallowEqual);

  const {
    getSiteList: { resultData: getSiteListData, isLoading: getSiteListLoading }
  } = candidateReducer;

  const {
    getGlobalSearch: { isLoading: getGlobalSearchLoading }
  } = candidateModalReducer;

  const siteListOptions = useMemo(() => {
    return getSiteListData?.map(({ Name }) => ({
      Name: Name,
      ObjectId: Name
    }));
  }, [getSiteListData]);

  const addFilter = filterType => {
    if (filterType === "RefereeFilter") {
      const obj = {
        refereeFilterData: {
          filterId: selectedRefereeFilterField.selectedFieldId,
          dropDownValue: selectedRefereeFilterField.selectedFieldDropDownValue,
          filterName: selectedRefereeFilterField.selectedFieldName,
          textValue: selectedRefereeFilterField.selectedFieldTextValue,
          fieldType: selectedRefereeFilterField.selectedFieldType,
          filterType
        }
      };

      handleFilterChange(obj);
      setSelectedRefereeFilterField(defaultSelectedField);
      // Force remount of Select components to clear any internal state of custom Select
      setRefereeSelectKey(prev => prev + 1);
    } else if (filterType === "ContactFilter") {
      const obj = {
        contactFilterData: {
          filterId: selectedContactFilterField.selectedFieldId,
          dropDownValue: selectedContactFilterField.selectedFieldDropDownValue,
          filterName: selectedContactFilterField.selectedFieldName,
          textValue: selectedContactFilterField.selectedFieldTextValue,
          fieldType: selectedContactFilterField.selectedFieldType,
          filterType
        }
      };

      handleFilterChange(obj);
      setSelectedContactFilterField(defaultSelectedField);

      // Force remount of Select components to clear any internal state of custom Select
      setContactSelectKey(prev => prev + 1);
    }
  };

  const handleRefereeFilter = filter => {
    if (!filter) return;

    const fieldDropDownOptions = {
      SiteOfInterest: siteListOptions
    };

    const [fieldId, fieldType] = filter.split("/");

    const field = REFEREE_FILTER_OPTIONS.find(option => option.ObjectId.startsWith(`${fieldId}/`));

    setSelectedRefereeFilterField({
      selectedFieldId: fieldId,
      selectedFieldName: field?.Name,
      selectedFieldType: fieldType,
      selectedFieldOptions: fieldDropDownOptions[fieldId] || null
    });
  };

  const handleContactFilter = filter => {
    if (!filter) return;

    const fieldDropDownOptions = {
      HasEmailAddresses: trueFalseOptions,
      HasPhoneNumbers: trueFalseOptions,
      HasAddresses: trueFalseOptions
    };

    const [fieldId, fieldType] = filter.split("/");
    const field = CONTACT_FILTER_OPTIONS.find(option => option.ObjectId.startsWith(`${fieldId}/`));

    setSelectedContactFilterField({
      selectedFieldId: fieldId,
      selectedFieldName: field?.Name,
      selectedFieldType: fieldType,
      selectedFieldOptions: fieldDropDownOptions[fieldId] || null
    });
  };

  const handleRefereeFieldChange = ({ fieldValue, fieldType }) => {
    if (fieldValue) {
      const payload = {};
      if (fieldType === SELECT_FIELD) {
        payload.selectedFieldDropDownValue = fieldValue;
      } else if (fieldType === TEXT_FIELD) {
        payload.selectedFieldTextValue = fieldValue.target.value;
      }
      setSelectedRefereeFilterField(prevState => ({
        ...prevState,
        ...payload
      }));
    }
  };

  const handleContactFieldChange = ({ fieldValue, fieldType }) => {
    if (fieldValue) {
      const payload = {};
      if (fieldType === SELECT_FIELD) {
        payload.selectedFieldDropDownValue = fieldValue;
      } else if (fieldType === TEXT_FIELD) {
        payload.selectedFieldTextValue = fieldValue.target.value;
      }
      setSelectedContactFilterField(prevState => ({
        ...prevState,
        ...payload
      }));
    }
  };

  // Disable "Add Filter" button if selected fields are incomplete
  const isFilterDisabled = (contactField, refereeField) => {
    if (contactField && contactField.selectedFieldId) {
      if (
        contactField.selectedFieldType === SELECT_FIELD &&
        !contactField.selectedFieldDropDownValue
      ) {
        return true;
      } else if (
        contactField.selectedFieldType === TEXT_FIELD &&
        !contactField.selectedFieldTextValue
      ) {
        return true;
      }
      return false;
    }

    if (refereeField && refereeField.selectedFieldId) {
      if (
        refereeField.selectedFieldType === SELECT_FIELD &&
        !refereeField.selectedFieldDropDownValue
      ) {
        return true;
      } else if (
        refereeField.selectedFieldType === TEXT_FIELD &&
        !refereeField.selectedFieldTextValue
      ) {
        return true;
      }
      return false;
    }

    return true;
  };

  // clear search data on unmount
  useEffect(() => {
    return () => {
      dispatch(detailClientSearchClear());
    };
  }, []);

  const loading = getGlobalSearchLoading || getSiteListLoading;

  const hasUnaddedFilter =
    Boolean(selectedRefereeFilterField.selectedFieldId) ||
    Boolean(selectedContactFilterField.selectedFieldId);

  return (
    <div className="global-search h-full">
      <Spin className="h-full" spinning={loading}>
        <Flex vertical className="filter-list h-full">
          {/* Referee Filters */}
          <Col span={24}>
            <Title level={5}>Referee Filter</Title>

            <Select
              placeholder="Search to Select"
              callback={filter => handleRefereeFilter(filter)}
              value={selectedRefereeFilterField.selectedFieldId}
              key={`referee-select-${refereeSelectKey}`}
              optionsArray={REFEREE_FILTER_OPTIONS}
              mode="single"
            />
          </Col>

          <FieldTypeGenerator
            selectedField={selectedRefereeFilterField}
            handleFieldChange={handleRefereeFieldChange}
          />

          <Row>
            <Button
              disabled={isFilterDisabled(null, selectedRefereeFilterField)}
              variant="primary" className="mt-2"
              onClick={() => addFilter("RefereeFilter")}
            >
              Add Referee Filter
            </Button>
          </Row>

          <Divider className="mt-2 mb-2" />

          {/* Contact Filters */}
          <Col span={24}>
            <Title level={5}>Contact Filter</Title>

            <Select
              placeholder="Search to Select"
              callback={filter => handleContactFilter(filter)}
              value={selectedContactFilterField.selectedFieldId}
              key={`contact-select-${contactSelectKey}`}
              optionsArray={CONTACT_FILTER_OPTIONS}
              mode="single"
            />
          </Col>

          <FieldTypeGenerator
            selectedField={selectedContactFilterField}
            handleFieldChange={handleContactFieldChange}
          />

          <Row>
            <Button
              disabled={isFilterDisabled(selectedContactFilterField, null)}
              variant="primary" className="mt-2"
              onClick={() => addFilter("ContactFilter")}
            >
              Add Contact Filter
            </Button>
          </Row>

          {/* Search/ Add/ Clear Filters*/}
          <Row className="mt-4">
            <Space>
              <Button
                variant="outline"
                disabled={detailFilter.length === 0}
                onClick={clearDetailFilter}
              >
                Clear Filters
              </Button>

              <Tooltip title={hasUnaddedFilter ? "Add filter above prior to search" : null}>
                <span>
                  <Button
                    variant="primary"
                    disabled={detailFilter.length === 0 || hasUnaddedFilter}
                    onClick={searchPerson}
                  >
                    Search
                  </Button>
                </span>
              </Tooltip>
            </Space>
          </Row>
        </Flex>
      </Spin>
    </div>
  );
};
export default Filters;
